import { useCallback, useEffect, useRef, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';
import type { CuratedInsight } from '@/types/inbox';

import {
	isInspirationReference,
	type Artifact,
	type InspirationReference,
} from '../../../bridge/types/artifact.ts';

export interface PendingCounts {
	curatedInsights: CuratedInsight[];
	/** Saved posts the daily insights job found, newest first. */
	jobInspirations: InspirationReference[];
	loading: boolean;
	refresh: () => Promise<void>;
}

export const usePendingCounts = (): PendingCounts => {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const [curatedInsights, setCuratedInsights] = useState<CuratedInsight[]>([]);
	const [jobInspirations, setJobInspirations] = useState<
		InspirationReference[]
	>([]);
	const [loading, setLoading] = useState(true);

	const refreshRef = useRef<() => Promise<void>>(async () => {});

	const refresh = useCallback(async () => {
		if (!userId) return;
		try {
			// One round trip for both, and a catch per leg: an artifacts failure
			// must not blank the insights the feed already holds, nor the reverse.
			//
			// Each leg catches to null, NOT to an empty list. A leg that catches
			// to [] answers "there is nothing" for "I could not ask", and the
			// setState below then replaced the rows already on screen with none.
			// One transient error while the customer switched threads erased the
			// finds from the feed silently, so it read as though the job found
			// nothing. Keeping the last good rows is the honest answer.
			const [curatedRes, artifactRes] = await Promise.all([
				bridgewayApi
					.get<CuratedInsight[]>(`/api/user/${userId}/curated_insights/`)
					.catch((err: unknown) => {
						logger.error('usePendingCounts curated_insights leg failed', err);
						return null;
					}),
				// The purpose-built route, not the whole artifacts list. That list
				// is the entire partition — hundreds of documents on a working
				// account — refetched on every chat change, which exhausted the
				// browser's connection budget and left this feed empty.
				bridgewayApi
					.get<
						Artifact[]
					>(`/api/user/${userId}/artifacts/search/job-inspirations`)
					.catch((err: unknown) => {
						logger.error('usePendingCounts job-inspirations leg failed', err);
						return null;
					}),
			]);
			if (curatedRes !== null) {
				setCuratedInsights(
					(Array.isArray(curatedRes) ? curatedRes : []).filter(
						(c) => c.status === 'new',
					),
				);
			}
			// The route already filters to job finds, orders newest first and
			// caps the count. The guard stays so a shape change cannot put a
			// customer's own saved link on the home screen.
			if (artifactRes !== null) {
				setJobInspirations(
					(Array.isArray(artifactRes) ? artifactRes : [])
						.filter(isInspirationReference)
						.filter((a) => Boolean(a.content.found_by_job_for?.trim())),
				);
			}
		} catch (err) {
			logger.error('usePendingCounts refresh failed', err);
		} finally {
			setLoading(false);
		}
	}, [userId, bridgewayApi]);

	refreshRef.current = refresh;

	useEffect(() => {
		if (!userId) return undefined;
		void refreshRef.current();
	}, [userId]);

	return {
		curatedInsights,
		jobInspirations,
		loading,
		refresh,
	};
};
